import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Plus, RotateCcw, Upload } from "lucide-react";
import { api } from "../api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const QUICK_FIELDS = [
  { k: "site.banner", label: "Site banner (empty = hidden)" },
  { k: "site.hours", label: "Hours" },
  { k: "site.address", label: "Address" },
  { k: "site.phone", label: "Phone" },
  { k: "site.footer_note", label: "Footer note" },
];

const SEO_PAGES = [
  { path: "/experience", label: "Home" },
  { path: "/menu", label: "Menu" },
  { path: "/experience/brands", label: "Brands" },
  { path: "/experience/arcade", label: "Arcade" },
  { path: "/experience/passport", label: "Passport" },
];

const inputCls = "w-full bg-white border border-black/15 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-[#B8860B]";
const btnGold = "bg-[#B8860B] text-white font-bold rounded-full px-4 py-2 text-[10px] tracking-widest disabled:opacity-50";
const btnGhost = "border border-black/20 text-black/60 font-bold rounded-full px-4 py-2 text-[10px] tracking-widest";

const QuickField = ({ k, label, value, onSaved, onUndone }) => {
  const [draft, setDraft] = useState(value ?? "");
  useEffect(() => setDraft(value ?? ""), [value]);
  const save = async () => {
    try {
      await api.post("/admin/content", { key: k, value: draft, label });
      toast.success(`${label} saved — live now`);
      onSaved(k, draft);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const undo = async () => {
    try {
      const r = await api.post("/admin/content/undo", { key: k });
      toast.success("Previous version restored");
      onUndone(k, r.data.value);
    } catch (e) { toast.error(e.response?.data?.detail || "Nothing to restore yet"); }
  };
  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
      <span className="text-[10px] font-black tracking-widest text-black/50 sm:w-44 shrink-0 uppercase">{label}</span>
      <input data-testid={`site-field-${k}`} value={draft} onChange={(e) => setDraft(e.target.value)} className={inputCls} />
      <div className="flex gap-2 shrink-0">
        <button data-testid={`site-save-${k}`} onClick={save} className={btnGold}>SAVE</button>
        <button data-testid={`site-undo-${k}`} onClick={undo} className={btnGhost} title="Restore previous version"><RotateCcw size={11} /></button>
      </div>
    </div>
  );
};

const SectionsBuilder = ({ sections, setSections }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const upload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("photo", file);
      const r = await api.post("/admin/site-image", fd);
      setSections([...sections, { type: "image", url: r.data.url }]);
      toast.success(`Photo added (resized to ${r.data.width}×${r.data.height})`);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); }
    setUploading(false);
  };
  return (
    <div className="space-y-2">
      {sections.map((s, i) => (
        <div key={i} className="flex items-start gap-2">
          {s.type === "image" ? (
            <img src={s.url.startsWith("/") ? `${BACKEND_URL}${s.url}` : s.url} alt="" className="h-16 rounded-lg border border-black/10" />
          ) : (
            <textarea data-testid={`page-section-${i}`} value={s.text} rows={s.type === "heading" ? 1 : 3}
              placeholder={s.type === "heading" ? "Heading…" : "Text…"}
              onChange={(e) => setSections(sections.map((x, j) => (j === i ? { ...x, text: e.target.value } : x)))}
              className={inputCls} />
          )}
          <button onClick={() => setSections(sections.filter((_, j) => j !== i))}
            className="text-black/40 text-[10px] font-bold shrink-0 mt-1" title="Remove this block (the page itself is never deleted)">REMOVE BLOCK</button>
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <button data-testid="page-add-heading" onClick={() => setSections([...sections, { type: "heading", text: "" }])} className={btnGhost}><Plus size={10} className="inline" /> HEADING</button>
        <button data-testid="page-add-text" onClick={() => setSections([...sections, { type: "text", text: "" }])} className={btnGhost}><Plus size={10} className="inline" /> TEXT</button>
        <button data-testid="page-add-image" onClick={() => fileRef.current?.click()} disabled={uploading} className={btnGhost}>
          <Upload size={10} className="inline" /> {uploading ? "UPLOADING…" : "PHOTO (auto-resized)"}
        </button>
        <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden"
          onChange={(e) => { if (e.target.files?.[0]) upload(e.target.files[0]); e.target.value = ""; }} />
      </div>
    </div>
  );
};

const PageRow = ({ p, reload }) => {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(p.title);
  const [sections, setSections] = useState(p.sections || []);
  const patch = async (body, okMsg) => {
    try {
      await api.patch(`/admin/pages/${p.slug}`, body);
      toast.success(okMsg);
      window.dispatchEvent(new Event("xp-pages-changed"));
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); }
  };
  const undo = async () => {
    try {
      await api.post("/admin/content/undo", { key: `page:${p.slug}` });
      toast.success("Previous version restored");
      window.dispatchEvent(new Event("xp-pages-changed"));
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Nothing to restore yet"); }
  };
  return (
    <div data-testid={`page-row-${p.slug}`} className={`rounded-2xl border p-4 ${p.hidden ? "border-black/10 bg-black/[0.03] opacity-70" : "border-black/15 bg-white"}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-bold text-sm flex-1 min-w-0">{p.title} <span className="text-black/35 font-normal">· /p/{p.slug}</span>{p.hidden && <span className="ml-2 text-[9px] font-black tracking-widest text-[#8A6D00]">HIDDEN</span>}</p>
        <select data-testid={`page-placement-${p.slug}`} value={p.placement}
          onChange={(e) => patch({ placement: e.target.value }, "Placement saved — live now")}
          className="bg-white border border-black/15 rounded-lg px-2 py-1.5 text-[11px] font-bold">
          <option value="footer">Footer</option>
          <option value="topnav">Top nav</option>
          <option value="bottomnav">Bottom navigation</option>
          <option value="none">No link (direct URL only)</option>
        </select>
        <button data-testid={`page-hide-${p.slug}`} onClick={() => patch({ hidden: !p.hidden }, p.hidden ? "Page is visible again" : "Page hidden — nothing is ever deleted")}
          className={btnGhost}>{p.hidden ? <><Eye size={10} className="inline" /> UNHIDE</> : <><EyeOff size={10} className="inline" /> HIDE</>}</button>
        <button data-testid={`page-edit-${p.slug}`} onClick={() => { setTitle(p.title); setSections(p.sections || []); setOpen(!open); }} className={btnGhost}>{open ? "CLOSE" : "EDIT"}</button>
      </div>
      {open && (
        <div className="mt-3 space-y-3">
          <input data-testid={`page-title-${p.slug}`} value={title} onChange={(e) => setTitle(e.target.value)} className={inputCls} />
          <SectionsBuilder sections={sections} setSections={setSections} />
          <div className="flex gap-2">
            <button data-testid={`page-save-${p.slug}`} onClick={() => { patch({ title, sections }, "Page saved — live now"); setOpen(false); }} className={btnGold}>SAVE PAGE</button>
            <button data-testid={`page-undo-${p.slug}`} onClick={undo} className={btnGhost}><RotateCcw size={10} className="inline" /> RESTORE PREVIOUS VERSION</button>
          </div>
        </div>
      )}
    </div>
  );
};

export const AdminSite = () => {
  const [content, setContent] = useState({});
  const [pages, setPages] = useState([]);
  const [log, setLog] = useState([]);
  const [seoPath, setSeoPath] = useState("/experience");
  const [newTitle, setNewTitle] = useState("");
  const [newPlacement, setNewPlacement] = useState("footer");
  const [newSections, setNewSections] = useState([]);
  const reload = useCallback(() => {
    api.get("/content").then((r) => setContent(r.data.overrides || {})).catch(() => {});
    api.get("/admin/pages").then((r) => setPages(r.data.pages || [])).catch(() => {});
    api.get("/admin/content/log").then((r) => setLog(r.data.log || [])).catch(() => {});
  }, []);
  useEffect(reload, [reload]);
  const setKey = (k, v) => setContent((c) => ({ ...c, [k]: v }));
  const createPage = async () => {
    try {
      await api.post("/admin/pages", { title: newTitle, placement: newPlacement, sections: newSections });
      toast.success("Page created — live now");
      setNewTitle(""); setNewSections([]);
      window.dispatchEvent(new Event("xp-pages-changed"));
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create the page"); }
  };
  const undoKey = async (k) => {
    try {
      await api.post("/admin/content/undo", { key: k });
      toast.success("Previous version restored — live now");
      window.dispatchEvent(new Event("xp-pages-changed"));
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Nothing to restore yet"); }
  };
  const hidden = pages.filter((p) => p.hidden);
  const seoTitleKey = `seo:${seoPath}:title`;
  const seoDescKey = `seo:${seoPath}:desc`;
  return (
    <div data-testid="admin-site" className="space-y-8 text-black">
      <p className="text-xs text-black/55 leading-relaxed rounded-2xl border border-black/10 bg-black/[0.03] p-3">
        Everything here is Passport-owned content and goes <b>live the moment you save</b> — no republish.
        The menu itself (items, categories, names, prices, photos, availability) stays with the POS: change those on the dish in the POS and push.
        Nothing here can be deleted — only hidden, and every save keeps the previous version.
      </p>

      <section>
        <p className="text-[10px] tracking-[0.3em] font-black text-black/50">SITE BASICS — banner, hours, address, phone, footer</p>
        <div className="mt-3 space-y-2.5">
          {QUICK_FIELDS.map((f) => (
            <QuickField key={f.k} k={f.k} label={f.label} value={content[f.k]} onSaved={setKey} onUndone={setKey} />
          ))}
        </div>
      </section>

      <section>
        <p className="text-[10px] tracking-[0.3em] font-black text-black/50">SEO TITLE & DESCRIPTION — per page</p>
        <select data-testid="seo-page-select" value={seoPath} onChange={(e) => setSeoPath(e.target.value)}
          className="mt-2 bg-white border border-black/15 rounded-lg px-2 py-1.5 text-[11px] font-bold">
          {SEO_PAGES.map((p) => <option key={p.path} value={p.path} label={p.label} />)}
          {pages.map((p) => <option key={p.slug} value={`/p/${p.slug}`} label={`Page: ${p.title}`} />)}
        </select>
        <div className="mt-2 space-y-2.5">
          <QuickField k={seoTitleKey} label="Browser-tab title" value={content[seoTitleKey]} onSaved={setKey} onUndone={setKey} />
          <QuickField k={seoDescKey} label="Description" value={content[seoDescKey]} onSaved={setKey} onUndone={setKey} />
        </div>
      </section>

      <section>
        <p className="text-[10px] tracking-[0.3em] font-black text-black/50">ADD A NEW PAGE</p>
        <div className="mt-3 space-y-3 rounded-2xl border border-black/15 bg-white p-4">
          <div className="flex flex-col sm:flex-row gap-2">
            <input data-testid="new-page-title" value={newTitle} onChange={(e) => setNewTitle(e.target.value)} placeholder="Page title" className={inputCls} />
            <select data-testid="new-page-placement" value={newPlacement} onChange={(e) => setNewPlacement(e.target.value)}
              className="bg-white border border-black/15 rounded-lg px-2 py-1.5 text-[11px] font-bold shrink-0">
              <option value="footer">Show in footer</option>
              <option value="topnav">Show in top nav</option>
              <option value="bottomnav">Show in bottom navigation</option>
              <option value="none">No link (direct URL only)</option>
            </select>
          </div>
          <SectionsBuilder sections={newSections} setSections={setNewSections} />
          <button data-testid="new-page-create" onClick={createPage} disabled={!newTitle.trim()} className={btnGold}>CREATE PAGE — LIVE ON SAVE</button>
        </div>
      </section>

      <section>
        <p className="text-[10px] tracking-[0.3em] font-black text-black/50">YOUR PAGES</p>
        <div className="mt-3 space-y-2.5">
          {pages.length === 0 && <p className="text-xs text-black/40">No pages yet — create one above.</p>}
          {pages.map((p) => <PageRow key={p.slug} p={p} reload={reload} />)}
        </div>
      </section>

      <section>
        <p className="text-[10px] tracking-[0.3em] font-black text-black/50">HIDDEN ITEMS — unhide any time, nothing is deleted</p>
        <div data-testid="hidden-items-list" className="mt-3 space-y-2">
          {hidden.length === 0 && <p className="text-xs text-black/40">Nothing is hidden.</p>}
          {hidden.map((p) => (
            <div key={p.slug} className="flex items-center gap-2 rounded-xl border border-black/10 bg-black/[0.03] px-3 py-2">
              <span className="text-xs font-bold flex-1">{p.title}</span>
              <button data-testid={`unhide-${p.slug}`} onClick={async () => {
                await api.patch(`/admin/pages/${p.slug}`, { hidden: false });
                toast.success("Visible again — live now");
                window.dispatchEvent(new Event("xp-pages-changed"));
                reload();
              }} className={btnGold}><Eye size={10} className="inline" /> UNHIDE</button>
            </div>
          ))}
        </div>
      </section>

      <section>
        <p className="text-[10px] tracking-[0.3em] font-black text-black/50">CHANGE LOG — what changed, when (Chicago time follows your device)</p>
        <div data-testid="content-change-log" className="mt-3 space-y-1.5">
          {log.length === 0 && <p className="text-xs text-black/40">No changes yet.</p>}
          {log.map((r, i) => (
            <div key={i} className="flex items-center gap-2 text-xs border-b border-black/5 pb-1.5">
              <span className="text-black/35 shrink-0 w-36">{new Date(r.ts).toLocaleString()}</span>
              <span className="font-bold shrink-0">{r.label}</span>
              <span className="text-black/55 flex-1 min-w-0 truncate">{r.summary}</span>
              {r.action !== "upload" && (
                <button data-testid={`log-undo-${i}`} onClick={() => undoKey(r.key)} className="text-[#B8860B] font-bold shrink-0" title="Restore previous version">
                  <RotateCcw size={11} className="inline" /> UNDO
                </button>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};
